import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ImageWithFallback } from './figma/ImageWithFallback';

interface ZoomableImageProps {
  src: string;
  alt: string;
  frameClassName?: string;
  imageClassName?: string;
  showErrorPlaceholder?: boolean;
  fallbackAspectRatio?: string;
  fallbackHeight?: string;
}

export function ZoomableImage({
  src,
  alt,
  frameClassName,
  imageClassName,
  showErrorPlaceholder = true,
  fallbackAspectRatio,
  fallbackHeight,
}: ZoomableImageProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);

  const close = () => {
    setIsOpen(false);
    setIsZoomed(false);
  };

  useEffect(() => {
    if (!isOpen || typeof window === 'undefined' || typeof document === 'undefined') return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  const modalImageStyle = useMemo(
    () => ({
      display: 'block',
      maxWidth: '92vw',
      maxHeight: '86vh',
      width: 'auto',
      height: 'auto',
      transform: isZoomed ? 'scale(1.8)' : 'scale(1)',
      transformOrigin: 'center center',
      transition: 'transform 180ms ease',
      cursor: isZoomed ? 'zoom-out' : 'zoom-in',
      borderRadius: '16px',
      boxShadow: '0 22px 48px rgba(0, 0, 0, 0.35)',
    }),
    [isZoomed]
  );

  const modal = isOpen ? (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={alt}
      onClick={close}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(17, 24, 39, 0.78)',
        padding: '24px',
        overflow: 'auto',
      }}
    >
      <button
        type="button"
        aria-label="Close image preview"
        onClick={close}
        style={{
          position: 'fixed',
          top: '16px',
          right: '16px',
          width: '40px',
          height: '40px',
          border: '1px solid rgba(255, 255, 255, 0.5)',
          borderRadius: '999px',
          background: 'rgba(17, 24, 39, 0.8)',
          color: '#fff',
          fontSize: '18px',
          cursor: 'pointer',
        }}
      >
        X
      </button>

      <div
        style={{
          minHeight: 'calc(100vh - 48px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '32px 12px 12px',
        }}
      >
        <img
          src={src}
          alt={alt}
          style={modalImageStyle}
          onClick={(event) => {
            event.stopPropagation();
            setIsZoomed((value) => !value);
          }}
        />
      </div>
    </div>
  ) : null;

  return (
    <>
      <div className={frameClassName}>
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          aria-label={alt}
          style={{
            display: 'block',
            width: '100%',
            padding: 0,
            border: 0,
            background: 'transparent',
            cursor: 'zoom-in',
          }}
        >
          <ImageWithFallback
            src={src}
            alt={alt}
            className={imageClassName}
            showErrorPlaceholder={showErrorPlaceholder}
            fallbackAspectRatio={fallbackAspectRatio}
            fallbackHeight={fallbackHeight}
          />
        </button>
      </div>

      {isOpen && typeof document !== 'undefined' ? createPortal(modal, document.body) : null}
    </>
  );
}
